import { useState } from "react";
import { Link } from "react-router";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import { authService } from "../../services/authService";

export default function ForgetPasswordForm() {
    const [formData, setFormData] = useState({
        email: ""
    });
    const [errors, setErrors] = useState({
        email: ""
    });
    const [isLoading, setIsLoading] = useState(false);
    const [apiError, setApiError] = useState("");
    const [requestSent, setRequestSent] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Limpar erros quando o usuário digita
        if (errors[name as keyof typeof errors]) {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }

        // Limpar erro da API quando o usuário faz alguma alteração
        if (apiError) {
            setApiError("");
        }
    };

    const validate = () => {
        const newErrors = {
            email: ""
        };
        let isValid = true;

        if (!formData.email.trim()) {
            newErrors.email = "Email é obrigatório";
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = "Email inválido";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setIsLoading(true);
        setApiError("");

        try {
            const response = await authService.forgotPassword(formData.email);

            if (response.success) {
                setRequestSent(true);
                setSuccessMessage(response.message || "Instruções de recuperação de senha foram enviadas para seu email.");
            } else {
                setApiError(response.message || "Não foi possível recuperar a senha. Tente novamente.");
            }
        } catch (error: any) {
            console.error("Erro ao solicitar recuperação de senha:", error);

            if (error.response && error.response.data && error.response.data.message) {
                setApiError(error.response.data.message);
            } else {
                setApiError("Ocorreu um erro ao processar sua solicitação. Por favor, tente novamente.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-col flex-1">
            <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
                <div>
                    <div className="mb-5 sm:mb-8">
                        <img
                            src="/images/logo/logo-icon.svg"
                            alt="Logo"
                            className="mx-auto"
                            width={48}
                            height={48}
                        />
                        <h1 className="mt-6 mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
                            Recuperar Senha
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Informe seu e-mail abaixo para recuperar sua senha.
                        </p>
                    </div>
                    <div>
                        {requestSent ? (
                            <div className="text-center">
                                <div className="mb-6 p-4 bg-success-100 text-success-700 rounded-md dark:bg-success-900/30 dark:text-success-500">
                                    <p>{successMessage}</p>
                                </div>
                                <p className="mb-6 text-gray-600 dark:text-gray-400">
                                    Verifique sua caixa de entrada e siga as instruções enviadas para recuperar sua senha.
                                </p>
                                <Link
                                    to="/signin"
                                    className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
                                >
                                    Voltar para Login
                                </Link>
                            </div>
                        ) : (
                            <>
                                {apiError && (
                                    <div className="p-3 mb-5 text-sm text-white rounded-lg bg-error-500">
                                        {apiError}
                                    </div>
                                )}
                                <form onSubmit={handleSubmit}>
                                    <div className="space-y-6">
                                        <div>
                                            <Label>
                                                Email <span className="text-error-500">*</span>{" "}
                                            </Label>
                                            <Input
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="Seu email"
                                                className={errors.email ? "border-error-500" : ""}
                                            />
                                            {errors.email && (
                                                <p className="mt-1 text-sm text-error-500">{errors.email}</p>
                                            )}
                                        </div>
                                        <div>
                                            <button
                                                type="submit"
                                                disabled={isLoading}
                                                className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600 disabled:opacity-70 disabled:cursor-not-allowed"
                                            >
                                                {isLoading ? "Processando..." : "Recuperar Senha"}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                                <div className="mt-5 w-full flex justify-between items-center">
                                    <div className="text-sm font-normal text-gray-700 dark:text-gray-400">
                                        <Link
                                            to="/signin"
                                            className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                                        >
                                            Voltar para Login
                                        </Link>
                                    </div>

                                    <div className="text-sm font-normal text-gray-700 dark:text-gray-400">
                                        <Link
                                            to="/signup"
                                            className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                                        >
                                            Criar uma nova conta
                                        </Link>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}